export const MARKER_SEPARATION_PX = 52;
export const MARKER_STACK_DISTANCE_PX = 72;
export const MARKER_STACK_DISTANCE_KM = 25;
export const FIVE_KM_DETAIL_THRESHOLD_PX = 160;

const EARTH_CIRCUMFERENCE_METERS = 40_075_016.686;
const MAP_TILE_SIZE = 512;

const ZERO_OFFSET = Object.freeze([0, 0]);

function geographicDistanceKm(left, right) {
  if (![left.lat, left.lng, right.lat, right.lng].every(Number.isFinite)) return Infinity;
  const radians = (degrees) => degrees * Math.PI / 180;
  const dLat = radians(right.lat - left.lat);
  const dLng = radians(right.lng - left.lng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(left.lat)) * Math.cos(radians(right.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** True once a 5 km privacy cell is large enough to read at city scale. */
export function shouldSeparateMarkers(zoom, latitude) {
  const latitudeScale = Math.max(0.05, Math.abs(Math.cos(Number(latitude) * Math.PI / 180)));
  const metersPerPixel = EARTH_CIRCUMFERENCE_METERS * latitudeScale
    / (MAP_TILE_SIZE * 2 ** Number(zoom));
  return 5000 / metersPerPixel >= FIVE_KM_DETAIL_THRESHOLD_PX;
}

/**
 * Collapse nearby screen-space anchors into one visual stack. A group stays
 * attached to its highest-priority member, so stacks do not jitter between
 * people as the globe moves and the visible stack anchors remain separated.
 */
export function stackMarkerGroups(
  markers,
  distance = MARKER_STACK_DISTANCE_PX,
  geographicLimitKm = MARKER_STACK_DISTANCE_KM
) {
  const ordered = [...markers].sort((left, right) => (
    (left.priority ?? 10) - (right.priority ?? 10)
    || String(left.id).localeCompare(String(right.id))
  ));
  const groups = [];
  const grid = new Map();
  const distanceSquared = distance ** 2;

  for (const marker of ordered) {
    const column = Math.floor(marker.x / distance);
    const row = Math.floor(marker.y / distance);
    const surface = marker.occluded ? 'covered' : 'visible';
    let nearest = null;
    let nearestSquared = Infinity;

    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (const group of grid.get(`${surface}:${column + dx}:${row + dy}`) ?? []) {
          const separationSquared = (group.x - marker.x) ** 2 + (group.y - marker.y) ** 2;
          const geographicallyNear = geographicDistanceKm(group, marker) <= geographicLimitKm;
          if (geographicallyNear
              && separationSquared < distanceSquared
              && separationSquared < nearestSquared) {
            nearest = group;
            nearestSquared = separationSquared;
          }
        }
      }
    }

    if (nearest) {
      nearest.ids.push(marker.id);
      continue;
    }

    const group = {
      x: marker.x, y: marker.y, lat: marker.lat, lng: marker.lng, ids: [marker.id],
    };
    groups.push(group);
    const key = `${surface}:${column}:${row}`;
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(group);
  }

  return groups.map((group) => group.ids);
}

export function sameMarkerGroups(left, right) {
  if (left === right) return true;
  if (!left || !right || left.length !== right.length) return false;
  return left.every((group, groupIndex) => (
    group.length === right[groupIndex].length
    && group.every((id, idIndex) => id === right[groupIndex][idIndex])
  ));
}

function cellKey(x, y) {
  return `${Math.floor(x / MARKER_SEPARATION_PX)}:${Math.floor(y / MARKER_SEPARATION_PX)}`;
}

function collides(x, y, grid) {
  const minimumSquared = MARKER_SEPARATION_PX ** 2;
  const column = Math.floor(x / MARKER_SEPARATION_PX);
  const row = Math.floor(y / MARKER_SEPARATION_PX);
  for (let dx = -1; dx <= 1; dx += 1) {
    for (let dy = -1; dy <= 1; dy += 1) {
      for (const point of grid.get(`${column + dx}:${row + dy}`) ?? []) {
        if ((point.x - x) ** 2 + (point.y - y) ** 2 < minimumSquared) return true;
      }
    }
  }
  return false;
}

function ringCandidates(ring) {
  const radius = ring * MARKER_SEPARATION_PX;
  const count = ring * 6;
  const phase = ring % 2 ? 0 : Math.PI / count;
  const result = [];
  for (let step = 0; step < count; step += 1) {
    const angle = phase + step * Math.PI * 2 / count;
    result.push([
      Math.round(Math.cos(angle) * radius),
      Math.round(Math.sin(angle) * radius),
    ]);
  }
  return result;
}

function candidates(rings) {
  const result = [ZERO_OFFSET];
  for (let ring = 1; ring <= rings; ring += 1) result.push(...ringCandidates(ring));
  return result;
}

/**
 * Greedily fan projected marker anchors into free screen-space slots. Priority
 * markers keep their true anchor; every subsequent avatar is placed far enough
 * from every avatar already on screen that their circles cannot overlap.
 */
export function layoutMarkerOffsets(markers, width, height) {
  const ordered = [...markers].sort((left, right) => (
    (left.priority ?? 10) - (right.priority ?? 10)
    || String(left.id).localeCompare(String(right.id))
  ));
  const rings = Math.max(10, Math.ceil(Math.sqrt(ordered.length || 1)) * 3 + 2);
  const options = candidates(rings);
  const margin = MARKER_SEPARATION_PX / 2;
  const grid = new Map();
  const offsets = {};

  for (const marker of ordered) {
    const anchorOnScreen = marker.x >= -margin && marker.x <= width + margin
      && marker.y >= -margin && marker.y <= height + margin;
    if (!anchorOnScreen || marker.occluded) {
      offsets[marker.id] = ZERO_OFFSET;
      continue;
    }

    const inViewport = options.find(([dx, dy]) => {
      const x = marker.x + dx;
      const y = marker.y + dy;
      return x >= margin && x <= width - margin
        && y >= margin && y <= height - margin
        && !collides(x, y, grid);
    });
    let offset = inViewport || options.find(([dx, dy]) => (
      !collides(marker.x + dx, marker.y + dy, grid)
    ));

    // The viewport can be physically too crowded, but screen-space itself is
    // unbounded. Keep expanding only in that exceptional case rather than ever
    // returning an overlapping slot.
    for (let ring = rings + 1; !offset; ring += 1) {
      offset = ringCandidates(ring).find(([dx, dy]) => (
        !collides(marker.x + dx, marker.y + dy, grid)
      ));
    }

    offsets[marker.id] = offset;
    const point = { x: marker.x + offset[0], y: marker.y + offset[1] };
    const key = cellKey(point.x, point.y);
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(point);
  }

  return offsets;
}

export function sameMarkerOffsets(left, right) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length && leftKeys.every((id) => (
    left[id]?.[0] === right[id]?.[0] && left[id]?.[1] === right[id]?.[1]
  ));
}
